---
title: 'LaunchDarkly feature flags'
metaTitle: 'Admin-UI Docs | Learn the basics > LaunchDarkly feature flags'
metaDescription: 'How to leverage the LaunchDarkly flags in the Admin-UI project'
---

## When to Use It

- User testing
- Gradual rollout
- Fix a reproducible bug only in production

For best practices on when and how to use feature flags, refer to the [Best Practices for using LaunchDarkly in your service](https://coveord.atlassian.net/wiki/spaces/PES/pages/5692031064/Best+Practices+for+using+LaunchDarkly+in+your+service) guide.

## Toggle a LaunchDarkly Flag via the UI

To access the LaunchDarkly UI, you can request access through your Okta dashboard. For instructions on create, update and delete operations, targeting, and setting up flags in the LaunchDarkly dashboard, refer to the [Using the LaunchDarkly UI](https://coveord.atlassian.net/wiki/spaces/PES/pages/5449154619/Using+the+LaunchDarkly+UI) guide.

Flags are created and managed entirely in the LaunchDarkly dashboard. They are no longer defined through Terraform or any other infrastructure-as-code configuration.

## Register a Flag in the Codebase

Commerce Hub uses a different layout: see [Commerce Hub](#commerce-hub).

1. Open the feature flag file of your application under `./configurations/{appName}/src/feature-flags`.

    There is one file per LaunchDarkly project, following the naming pattern `{ProjectName}LaunchDarklyFeatureFlags.ts`. The `{ProjectName}` matches one of the projects listed in the `LDProjectName` map from `@core/feature-flags` (e.g. `PlatformLaunchDarklyFeatureFlags.ts` for `LDProject.PLATFORM_UI`, `CommerceServiceLaunchDarklyFeatureFlags.ts` for `LDProject.COMMERCE_SERVICE`). Pick the file matching the project your flag belongs to; an application can have several such files if it reads flags from multiple projects.

2. Add your key in that file's exported object, mapping a `PascalCase` name to the flag key:

    ```tsx
    export const PlatformLaunchDarklyFeatureFlags = {
        YourFeatureFlag: 'your-feature-flag-key',
    } as const;
    ```

## Use a Flag in Code

### In a component: `useFeatureFlag` hook

In React components, always prefer the `useFeatureFlag` hook over `LaunchDarklyFeatureFlags.isActive`:

```tsx
import {useFeatureFlag} from '@core/feature-flags';
import {PlatformLaunchDarklyFeatureFlags} from '@configurations/platform';
import {FunctionComponent} from 'react';

const MyComponent: FunctionComponent = () => {
    const isEnabled = useFeatureFlag(PlatformLaunchDarklyFeatureFlags.YourFeatureFlag);

    if (!isEnabled) {
        return <FallbackComponent />;
    }

    return <FeatureComponent />;
};
```

**Why prefer `useFeatureFlag` over `LaunchDarklyFeatureFlags.isActive`:**

- **Reactive** — automatically re-renders the component when the flag value changes in the LaunchDarkly dashboard.

`LaunchDarklyFeatureFlags.isActive` is a static read that does not trigger re-renders and should only be used outside of React components (e.g., in utility functions or configuration).

### In a Guard: `hasFeatureFlag`

When checking a flag inside a `Guard`'s `canRender` or `canEdit` callback, use the `hasFeatureFlag` parameter provided by the callback:

You can also specify a LaunchDarkly project if the flag belongs to a project other than the default `LDProject.PLATFORM_UI`:

```tsx
<Guard
    canRender={({hasFeatureFlag}) =>
        hasFeatureFlag(CommerceServiceLaunchDarklyFeatureFlags.YourFeatureFlag, LDProject.COMMERCE_SERVICE)
    }
>
    <FeatureComponent />
</Guard>
```

The available projects are defined by the `LDProject` enum, and their display names by the `LDProjectName` map, both in `@core/feature-flags`. `useFeatureFlag` and `LaunchDarklyFeatureFlags.isActive` accept an `LDProject` as their second argument in the same way.

## Remove a Flag

Removing a flag should be done in 2 steps.
This helps prevent potential feature flag caching issues.

### Step 1

Remove all usages of the flag in the code & tests.

1. Remove the entry from the feature flag file under `./configurations/{appName}/src/feature-flags` where you registered it.
2. Remove all the conditions that check the flag validation.
3. Remove obsolete imports, if any.
4. Open a pull request with those changes.

### Step 2

1. Once the previous PR is merged, deprecate the flag from the LaunchDarkly dashboard to prevent any future usage. See [Deprecating Flags](https://coveord.atlassian.net/wiki/spaces/PES/pages/5692031064/Best+Practices+for+using+LaunchDarkly+in+your+service#Deprecating-Flags)

## Commerce Hub

Commerce Hub flags live in `@configurations/commerce-hub/feature-flags`, grouped by feature, and each flag must name the Jira ticket that will remove it.

### Pick the File

There is one folder per LaunchDarkly project under `configurations/commerce-hub/src/feature-flags`:

| Folder                        | Export                                             | LaunchDarkly project                                         |
| ----------------------------- | -------------------------------------------------- | ------------------------------------------------------------ |
| `commerce-hub-frontend/`      | `CommerceHubLaunchDarklyFeatureFlags`              | `LDProject.COMMERCE_UI`                                      |
| `commerce-hub-backend/`       | `CommerceHubBackendLaunchDarklyFeatureFlags`       | `commerce-ui`, for flags only `@backends/commerce-hub` reads |
| `commerce-service/`           | `CommerceServiceLaunchDarklyFeatureFlags`          | `LDProject.COMMERCE_SERVICE`                                 |
| `catalog-management-service/` | `CatalogManagementServiceLaunchDarklyFeatureFlags` | `LDProject.CATALOG_MANAGEMENT_SERVICE`                       |

`commerce-hub-frontend/` has one file per feature (`OverviewPageFlags.ts`, `PerformanceMetricsFlags.ts`, `RelevanceAgentFlags.ts`...). Add your flag to the matching file. For a new feature, create a `{Feature}Flags.ts` file and spread it into `CommerceHubLaunchDarklyFeatureFlags` in `commerce-hub-frontend/index.ts`. A single flag that fits no feature can go in the inline list in that `index.ts`.

### Add a Flag

1. Create the flag in the LaunchDarkly dashboard and target your test organization.
2. File the ticket that will remove the flag, in the Jira project of the team that owns the feature.
3. Register the flag with `defineFeatureFlags`. Type-check fails if `deprecationTicket` is missing or isn't a Jira link:

    ```ts
    import {defineFeatureFlags} from '../defineFeatureFlags.js';

    export const PerformanceMetricsFlags = defineFeatureFlags({
        ItemMetricsV2: {
            key: 'item-metrics-v2',
            deprecationTicket: 'https://coveord.atlassian.net/browse/CCRE-4175',
        },
    });
    ```

4. Add a hook in `pages/commerce-hub/src/hooks/feature-flags/useIs{Feature}Enabled.ts`, and read the flag only through it:

    ```ts
    import {CommerceHubLaunchDarklyFeatureFlags} from '@configurations/commerce-hub/feature-flags';
    import {LDProject, useFeatureFlag} from '@core/feature-flags';

    export const useIsItemMetricsV2Enabled = (): boolean =>
        useFeatureFlag(CommerceHubLaunchDarklyFeatureFlags.ItemMetricsV2, LDProject.COMMERCE_UI);
    ```

    Always pass the `LDProject`: the default is `PLATFORM_UI`. When the feature also depends on other conditions, combine them in the hook (see `useIsSpotlightContentEnabled`).

5. Unit test the hook by mocking `useFeatureFlag`, covering the flag on, off and missing. Copy `useIsItemMetricsV2Enabled.unit.spec.ts`.

### Gate the Feature

- **Component:** call the hook and keep the previous behaviour when it returns `false`.
- **Page:** the route is registered whatever the flag value, so pass the hook's result to the `Page`'s `canRender`, as `OverviewPage` does.
- **Navigation entry or `Guard`:** use `hasFeatureFlag(flag, LDProject.COMMERCE_UI)` in `canRender` (see `StorefrontPreviewAccessHelper`).
- **Backend:** read the flag with the LaunchDarkly client and default to `false`, as in `backends/commerce-hub/src/domains/health/services/feature-flags.ts`:

    ```ts
    launchDarkly.client.getBooleanFlagValue(
        CommerceHubBackendLaunchDarklyFeatureFlags.ProductHealthNext,
        false,
        LaunchDarklyContext.of(organizationContext(organizationId)),
    );
    ```

In the pull request's "How to Test" section, name the organization that has the flag turned on.

### Retire a Flag

Follow [Remove a Flag](#remove-a-flag), and in step 1 also delete the hook and its test. Close the deprecation ticket once the flag is deprecated in LaunchDarkly.

### Examples

- [admin-ui#16992](https://github.com/coveo-platform/admin-ui/pull/16992): new page behind a flag (flag, hook, navigation entry and page).
- [admin-ui#21782](https://github.com/coveo-platform/admin-ui/pull/21782): flag and hook only, with tests.
- [admin-ui#21602](https://github.com/coveo-platform/admin-ui/pull/21602): grouping by feature and `defineFeatureFlags`.

## Exception: Interface Builder

The Interface Builder does not use `@core/feature-flags`. It initializes its own LaunchDarkly clients and exposes flags through a local `useLDFlags` hook. See its [Feature Flags documentation](https://github.com/coveo-platform/admin-ui/blob/master/apps/interface-builder/docs/FEATURE-FLAGS.md) for details. This is a known inconsistency rather than an intentional design choice.
